import type { PlayerConfig, SurfaceConfig } from "../config/types";

export interface MoveState {
    x: number;
    y: number;
    heading: number;
    speed: number;
    boost: number;
    boostOn: boolean;
    pedalling: boolean;
    braking: boolean;
    skidding: boolean;
}

export interface MoveInput {
    steer: number;
    targetHeading: number | null;
    pedal: boolean;
    brake: boolean;
    boostToggle: boolean;
}

const RAD = Math.PI / 180;
const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));

export function createMoveState(cfg: PlayerConfig): MoveState {
    return {
        x: 0, y: 0, heading: 0, speed: cfg.startSpeed,
        boost: 1, boostOn: false, pedalling: false, braking: false, skidding: false,
    };
}

export function refillBoost(state: MoveState, amount: number): MoveState {
    return { ...state, boost: clamp(state.boost + amount, 0, 1) };
}

function stepHeading(heading: number, input: MoveInput, maxTurn: number, smoothing: number, dt: number) {
    if (input.targetHeading === null) return clamp(heading + input.steer * maxTurn, -90, 90);
    const eased = (input.targetHeading - heading) * Math.min(1, smoothing * dt);
    return clamp(heading + clamp(eased, -maxTurn, maxTurn), -90, 90);
}

function stepBoost(state: MoveState, toggle: boolean, dt: number, cfg: PlayerConfig) {
    let boostOn = toggle ? !state.boostOn && state.boost > 0 : state.boostOn;
    const change = boostOn ? -cfg.boostDrainPerS : cfg.boostPassiveRefillPerS;
    const boost = clamp(state.boost + change * dt, 0, 1);
    if (boost === 0) boostOn = false;
    return { boost, boostOn };
}

export function roughPenalty(surface: SurfaceConfig, speed: number, turnFraction: number, cfg: PlayerConfig) {
    const slowness = Math.max(0, 1 - speed / cfg.maxSpeed);
    return surface.rough * slowness * (1 + 2 * turnFraction);
}

export function speedCap(boostOn: boolean, pedal: boolean, surface: SurfaceConfig, cfg: PlayerConfig) {
    const base = boostOn ? cfg.boostSpeed : cfg.maxSpeed + (pedal ? cfg.pedalCapBonus : 0);
    return base * surface.speedMod;
}

export function stepMovement(
    state: MoveState,
    input: MoveInput,
    surface: SurfaceConfig,
    dt: number,
    cfg: PlayerConfig,
): MoveState {
    const skidding = input.brake && state.speed > cfg.skidThreshold;
    const steerFactor = skidding ? cfg.skidSteerFactor : 1;
    const maxTurn = cfg.turnRate * surface.grip * steerFactor * dt;
    const heading = stepHeading(state.heading, input, maxTurn, cfg.headingSmoothing, dt);
    const turnFraction = dt > 0 ? Math.abs(heading - state.heading) / (cfg.turnRate * dt) : 0;

    const { boost, boostOn } = stepBoost(state, input.boostToggle, dt, cfg);
    const cap = speedCap(boostOn, input.pedal, surface, cfg);
    const downhill = Math.cos(heading * RAD);

    let accel = cfg.gravityAccel * downhill
        - cfg.traverseFriction * (1 - downhill)
        - surface.drag
        - state.speed * cfg.sharpTurnLoss * turnFraction * (surface.turnLoss ?? 1)
        - roughPenalty(surface, state.speed, turnFraction, cfg);
    if (input.pedal) accel += cfg.pedalAccel * Math.max(0.2, 1 - state.speed / cap);
    if (boostOn) accel += cfg.boostAccel;
    if (skidding) accel -= cfg.skidBraking;
    else if (input.brake) accel -= cfg.braking;

    let speed = state.speed + accel * dt;
    if (speed > cap) {
        speed = state.speed <= cap ? cap : speed - (speed - cap) * Math.min(1, cfg.overCapDecay * dt);
    }
    speed = Math.max(0, speed);

    // Without the brake the rider never stalls: they pedal up to a slow roll, even fully across the slope.
    const autoPedal = !input.brake && speed < cfg.minRollSpeed;
    if (autoPedal) speed = Math.min(cfg.minRollSpeed, Math.max(speed, state.speed + cfg.pedalAccel * dt));

    return {
        x: state.x + speed * Math.sin(heading * RAD) * dt,
        y: state.y + speed * downhill * dt,
        heading,
        speed,
        boost,
        boostOn,
        pedalling: input.pedal || autoPedal,
        braking: input.brake && !skidding,
        skidding,
    };
}
